import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';

import type { Placement, PositioningStrategy } from '@popperjs/core';
import cn from 'classnames';
import { usePopper } from 'react-popper';

import {
  useArrowModifier,
  useFlipModifier,
  useOffsetModifier,
} from './popperHelpers';
import styles from './Tooltip.module.css';
import { useOrionUID } from '../../helpers/orionUID';
import { Icon } from '../icon/Icon';

export type TooltipProps = {
  content: ReactNode;
  variant?: 'primary' | 'secondary';
  open?: boolean;
  openDelay?: number;
  closeDelay?: number;
  placement?: Placement;
  strategy?: PositioningStrategy;
  dataTestId?: string;
} & DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>;

export const Tooltip = ({
  content,
  variant = 'primary',
  open: externalOpen,
  openDelay,
  closeDelay,
  placement,
  strategy,
  dataTestId,
  children,
  ...props
}: TooltipProps) => {
  const orionUID = useOrionUID();
  const [open, setOpen] = useState(externalOpen || false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLSpanElement | null>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>();
  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    placement: placement,
    strategy: strategy,
    modifiers: [
      useArrowModifier(arrowElement),
      useFlipModifier(),
      useOffsetModifier(),
    ],
  });

  const willOpen = useRef<NodeJS.Timeout>();

  const showTooltip = useCallback(() => {
    willOpen.current = setTimeout(async () => {
      setOpen(true);
      await update?.();
    }, openDelay);
  }, [setOpen, update, openDelay]);

  const hideTooltip = useCallback(() => {
    clearTimeout(willOpen.current);
    setTimeout(() => {
      setOpen(false);
    }, closeDelay);
  }, [setOpen, closeDelay]);

  useEffect(() => {
    if (externalOpen !== undefined) {
      externalOpen ? showTooltip() : hideTooltip();
    }
  }, [externalOpen, showTooltip, hideTooltip]);

  const keyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        hideTooltip();
      }
    },
    [hideTooltip],
  );

  // hide tooltip on escape for a11y
  useLayoutEffect(() => {
    referenceElement?.addEventListener('keydown', keyDown);
    return () => referenceElement?.removeEventListener('keydown', keyDown);
  }, [referenceElement, hideTooltip, keyDown]);

  return (
    <>
      <span
        data-testid={dataTestId}
        className={styles.refElemWrapper}
        aria-describedby={`tooltip_${orionUID}`}
        ref={setReferenceElement}
        onMouseEnter={() => {
          if (externalOpen === undefined) {
            showTooltip();
          }
        }}
        onFocus={() => {
          if (externalOpen === undefined) {
            showTooltip();
          }
        }}
        onMouseLeave={() => {
          if (externalOpen === undefined) {
            hideTooltip();
          }
        }}
        onBlur={() => {
          if (externalOpen === undefined) {
            hideTooltip();
          }
        }}
      >
        {children ? (
          children
        ) : (
          <Icon
            className={cn(styles.icon, {
              [styles.secondaryTooltip]: variant === 'secondary',
            })}
            tabIndex={0}
            icon="questionMarkFilled"
          />
        )}
      </span>

      <div
        id={`tooltip_${orionUID}`}
        role="tooltip"
        data-show={open}
        style={popperStyles.popper}
        {...props}
        {...attributes.popper}
        className={cn(styles.tooltip, props.className)}
        ref={setPopperElement}
      >
        <div className={styles.tooltipContent}>{content}</div>
        <div
          ref={setArrowElement}
          className={styles.arrow}
          style={popperStyles.arrow}
        />
      </div>
    </>
  );
};

Tooltip.displayName = 'Tooltip';
